import { TimeAxisMath } from './time-axis-math.js';

export const DEFAULT_AXIS_WINDOW_DAYS = 730;
export const MS_PER_DAY_CONST = 24 * 60 * 60 * 1000;
export const AXIS_PAD_BEFORE_DAYS = 30;
export const AXIS_PAD_AFTER_DAYS = 180;

export type GanttAxisWindowRow = {
  rowId: string;
};

export type GanttAxisWindow = {
  fromMs: number;
  originMs: number;
  toMs: number;
};

export function computeGanttAxisWindow(input: {
  endCol: string | undefined;
  now: number;
  readCellValue: (rowId: string, propertyId: string) => unknown;
  rows: readonly GanttAxisWindowRow[];
  startCol: string | undefined;
}): GanttAxisWindow {
  const defaultFrom =
    input.now - (DEFAULT_AXIS_WINDOW_DAYS / 2) * MS_PER_DAY_CONST;
  const defaultTo = defaultFrom + DEFAULT_AXIS_WINDOW_DAYS * MS_PER_DAY_CONST;
  const { minMs, maxMs } = collectGanttBarRange(input);
  const extFrom =
    minMs != null
      ? Math.min(defaultFrom, minMs - AXIS_PAD_BEFORE_DAYS * MS_PER_DAY_CONST)
      : defaultFrom;
  const extTo =
    maxMs != null
      ? Math.max(defaultTo, maxMs + AXIS_PAD_AFTER_DAYS * MS_PER_DAY_CONST)
      : defaultTo;
  const originMs = TimeAxisMath.floorToDayUtc(extFrom);

  return {
    fromMs: originMs,
    originMs,
    toMs: TimeAxisMath.floorToDayUtc(extTo),
  };
}

function collectGanttBarRange(input: {
  endCol: string | undefined;
  readCellValue: (rowId: string, propertyId: string) => unknown;
  rows: readonly GanttAxisWindowRow[];
  startCol: string | undefined;
}): {
  maxMs: number | undefined;
  minMs: number | undefined;
} {
  if (!input.startCol || !input.endCol) {
    return { maxMs: undefined, minMs: undefined };
  }

  let minMs: number | undefined;
  let maxMs: number | undefined;
  for (const row of input.rows) {
    const start = input.readCellValue(row.rowId, input.startCol);
    const end = input.readCellValue(row.rowId, input.endCol);
    if (typeof start === 'number') {
      minMs = minMs == null ? start : Math.min(minMs, start);
      maxMs = maxMs == null ? start : Math.max(maxMs, start);
    }
    if (typeof end === 'number') {
      minMs = minMs == null ? end : Math.min(minMs, end);
      maxMs = maxMs == null ? end : Math.max(maxMs, end);
    }
  }

  return { maxMs, minMs };
}
